/**
 * What the Defender board renders today: the consent position, in full, with the
 * exact permissions to grant and the exact two places to grant them.
 *
 * **This is the most important component in the feature**, because the board's
 * normal appearance today is not `ok`. The four read-only Graph application
 * permissions it needs have not been consented yet — a colleague grants them
 * later — so `/defender/permissions` exists specifically to drive this panel. It
 * is the one route that makes no Graph call at all, which is why it answers with
 * no credential, cannot be throttled, and can state the ask while every data
 * panel is dark.
 *
 * Three honesty rules do the real work here:
 *
 * 1. **An empty `missing_permissions` is not "nothing is missing".** The list is
 *    populated only by a measured 403. So each permission carries one of three
 *    verdicts — granted, refused, or *not yet exercised* — and the third is
 *    rendered as unknown rather than as a fault or as a pass. Before any read
 *    has been attempted the honest statement is "nothing has been proved either
 *    way", and that is what this panel says.
 * 2. **`granted_count` is nullable and stays nullable.** Null until at least one
 *    permission has been probed, because `0 of 4` would read as "all four
 *    refused" — a much louder claim than "we have not found out yet".
 * 3. **Two places, or the board stays dark.** The deployed app authenticates as
 *    the managed identity, not as the app registration, so consenting only the
 *    app registration leaves the running board unable to read anything. That is
 *    the single most likely way this grant gets done and still does not work, so
 *    it is stated as a step rather than a footnote.
 *
 * When consent lands, nothing here changes and nothing ships: the same calls
 * start returning 200, the panels light up on their own next poll, and this
 * panel collapses to a green line.
 *
 * Extends the pattern in `features/security/not-configured.tsx` — same
 * "name each missing piece, say where it comes from, say in what order" shape —
 * with the advantage that this backend can name the *specific* permissions.
 */

import {
  CircleCheck,
  CircleEllipsis,
  Icon,
  type IconComponent,
  ListChecks,
  ShieldCheck,
  ShieldOff,
  UserRoundCog,
} from "@norican/icons";
import { Alert, Badge, Card, Separator, Statistic, Typography } from "@norican/react";

import type { DefenderPermissions, DefenderPermissionStatus } from "@/lib/api";
import type { StatusTone } from "@/lib/charts/config";

import { SETUP_ANCHOR } from "./params";
import {
  ACCESS_COPY,
  type DefenderPanelState,
  EM_DASH,
  formatCount,
  NEVER_REQUESTED_ICON,
} from "./status";

/**
 * The app registration the four permissions are granted on, and the managed
 * identity the same four app roles must be assigned to.
 *
 * Named in the UI because "grant the Defender permissions" is not a ticket
 * anyone can action and "add these four to NO-AR-NOVA, then assign the same four
 * to nova-app-dev" is. Both names also appear verbatim in the backend's own
 * `status_detail`, which is rendered unparaphrased above them.
 */
const APP_REGISTRATION = "NO-AR-NOVA";
const MANAGED_IDENTITY = "nova-app-dev";

/** What a read has actually proved about one permission. */
type PermissionVerdict = "granted" | "refused" | "unprobed";

interface VerdictCopy {
  badge: string;
  tone: StatusTone;
  /** `Icon` has no `neutral` tone, so the unknown verdict deliberately has none. */
  iconTone?: "success" | "warning" | "danger" | "info";
  icon: IconComponent;
  note: string;
}

/**
 * The three verdicts, and why the third is not a fourth colour of failure.
 *
 * `unprobed` is the state of all four permissions right now. It is not `denied`:
 * NOVA has not been refused, it has not asked. Rendering it as a fault would
 * invent evidence, and rendering it as a pass would hide the ask — so it is
 * rendered as what it is, unknown, and the ask is stated separately below.
 */
const VERDICT_COPY: Record<PermissionVerdict, VerdictCopy> = {
  granted: {
    badge: "Granted",
    tone: "success",
    iconTone: "success",
    icon: CircleCheck,
    note: "A Graph read has succeeded using this permission. Measured, not assumed.",
  },
  refused: {
    badge: "Refused — 403",
    tone: "warning",
    iconTone: "warning",
    icon: ShieldOff,
    note:
      "Microsoft Graph refused a read that needed it. This is the measured, actionable case: this " +
      "is a permission to grant.",
  },
  unprobed: {
    badge: "Not yet exercised",
    tone: "neutral",
    icon: CircleEllipsis,
    note:
      "No read has needed it yet, so nothing has been proved — neither that it is missing nor that " +
      "it is granted. It is listed because it is what this board asks for, not because it has failed.",
  },
};

/** The steps a human takes, in the order they have to happen. */
const GRANT_STEPS: readonly { title: string; detail: string }[] = [
  {
    title: `Add the permissions on the app registration ${APP_REGISTRATION}`,
    detail:
      "All four are type Application (app-only), all four are read-only, and all four are on the " +
      "Graph v1.0 API. Add them, then grant admin consent. A Global Administrator or Privileged " +
      "Role Administrator can do it; no code change and no redeploy is involved.",
  },
  {
    title: `Assign the same four app roles to the managed identity ${MANAGED_IDENTITY}`,
    detail:
      "Both places, please — this is the step that gets skipped. The deployed app authenticates as " +
      "the managed identity, not as the app registration, so consenting only the registration " +
      "leaves this board exactly as dark as it is now while looking done.",
  },
  {
    title: "Allow up to about 24 hours",
    detail:
      "A role assignment can lag that long before it reaches a managed-identity token. The board " +
      "stays dark in the meantime and that is expected, not a second failure.",
  },
  {
    title: "Nothing else",
    detail:
      "No deployment, no configuration change, no frontend change. A failed read is negative-cached " +
      "for 30 seconds, so the first successful call after the grant lands lights the board up on " +
      "its own next poll.",
  },
];

interface DefenderSetupPanelProps {
  /** `/defender/permissions` — the constant ask plus whatever reads have proved. */
  permissions: DefenderPermissions;
  /**
   * Every data panel's state.
   *
   * Read for two things the permissions route cannot know on its own within one
   * page load: the 403s the four panels have measured *this* render (the
   * permissions route reflects a process-wide registry that may be read before
   * the panels answer), and how many panels are dark right now.
   */
  panels: readonly { label: string; state: DefenderPanelState }[];
}

export function DefenderSetupPanel({ permissions, panels }: DefenderSetupPanelProps) {
  // Union of every permission Graph has actually refused with a 403 — the
  // permissions route's own observation plus what the panels measured on this
  // render. Only 403s land in `missing_permissions`; a 404, a 400, a 5xx and a
  // missing credential all leave it empty, which is why an empty union below is
  // never rendered as "nothing is missing".
  const refused = new Set<string>(permissions.missing_permissions);
  for (const panel of panels) {
    for (const name of panel.state.missing_permissions) {
      refused.add(name);
    }
  }

  const verdicts = permissions.required.map((entry) => ({
    entry,
    verdict: verdictFor(entry, refused),
  }));
  const refusedCount = verdicts.filter((row) => row.verdict === "refused").length;
  const grantedCount = verdicts.filter((row) => row.verdict === "granted").length;
  const requiredCount = permissions.required.length;

  // Whether *any* read has proved anything about *any* of these permissions.
  // False is the state today, and it is the reason no count on this card may
  // render as a measured zero: a zero counted over requests that were never
  // sent is a finding NOVA has not earned.
  const probed =
    permissions.granted_count != null || verdicts.some((row) => row.verdict !== "unprobed");

  // A panel is dark for a reason of its own; grouped so the headline can say
  // whether every panel is waiting on consent or on several different things.
  const darkPanels = panels.filter((panel) => panel.state.access_state !== "ok");
  const consentBlocked = darkPanels.filter(
    (panel) => panel.state.access_state === "consent_missing",
  );

  const headlineTone: StatusTone =
    grantedCount === requiredCount && requiredCount > 0
      ? "success"
      : refusedCount > 0 || darkPanels.length > 0
        ? "warning"
        : "neutral";

  return (
    <section id={SETUP_ANCHOR} className="flex scroll-mt-6 flex-col gap-4">
      {/* The backend's own one-liner, verbatim — it already names the app
          registration, the identity and the count. Paraphrasing it would lose
          the exact strings someone has to paste somewhere. */}
      <Alert tone={headlineTone} title="This board is dark on purpose, and here is exactly why">
        {permissions.status_detail}
      </Alert>

      <Card variant="outline" accent="left" accentTone={headlineTone}>
        <Card.Header>
          <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
            <div className="flex items-center gap-3">
              <Icon icon={ShieldCheck} size="md" />
              <Typography variant="heading-4" as="h2">
                The four permissions this board asks for
              </Typography>
            </div>
            <Badge tone={headlineTone} variant="subtle" size="sm">
              {permissions.granted_count == null
                ? `${EM_DASH} of ${formatCount(requiredCount)} proved`
                : `${formatCount(permissions.granted_count)} of ${formatCount(requiredCount)} granted`}
            </Badge>
          </div>
        </Card.Header>
        <Card.Body>
          <div className="flex flex-col gap-5">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <Statistic
                label="Permissions granted"
                // Null, not 0: `0 of 4` claims all four were refused, which is a
                // far louder statement than "we have not found out yet".
                value={
                  permissions.granted_count == null
                    ? EM_DASH
                    : `${formatCount(permissions.granted_count)} / ${formatCount(requiredCount)}`
                }
                tone={permissions.granted_count == null ? "neutral" : headlineTone}
                size="lg"
                caption={
                  permissions.granted_count == null
                    ? "Not one has been exercised yet, so nothing is known about consent — this is unknown, not zero"
                    : "Proved by a read that actually succeeded"
                }
              />
              <Statistic
                label="Refused with a 403"
                // A measured `0` and "nothing has been asked" are different
                // findings, so they do not share a figure. Before any read has
                // been attempted this is an em dash: a zero would be a count of
                // refusals from a set of requests that was never sent, which is
                // exactly the reassuring-looking zero §5 forbids.
                value={probed ? formatCount(refusedCount) : EM_DASH}
                tone={refusedCount > 0 ? "warning" : "neutral"}
                size="lg"
                caption={
                  refusedCount > 0
                    ? "Named below — these are measured refusals, and the actionable ones"
                    : probed
                      ? "Reads have gone out and none came back 403. That rules out consent for what was exercised, and nothing else."
                      : "Nothing has been refused, because nothing has been asked. Not the same as nothing missing."
                }
              />
              <Statistic
                label="Panels waiting on this"
                value={`${formatCount(darkPanels.length)} / ${formatCount(panels.length)}`}
                tone={darkPanels.length > 0 ? "warning" : "success"}
                size="lg"
                caption={
                  consentBlocked.length === darkPanels.length && darkPanels.length > 0
                    ? "Every dark panel is waiting on consent — one grant, four panels"
                    : darkPanels.length > 0
                      ? "Not all for the same reason — each panel states its own below"
                      : "Every panel is reading live"
                }
              />
            </div>

            <Separator />

            <ul className="flex flex-col gap-5">
              {verdicts.map(({ entry, verdict }) => (
                <li key={entry.name}>
                  <PermissionRow entry={entry} verdict={verdict} />
                </li>
              ))}
            </ul>

            {/* What the empty list means *in this state*, so it can never be
                read as reassurance. §5. */}
            <Alert
              tone="neutral"
              title="Why an empty 'missing' list is not the same as 'nothing is missing'"
            >
              {ACCESS_COPY[darkPanels[0]?.state.access_state ?? "consent_missing"].evidenceNote}
            </Alert>
          </div>
        </Card.Body>
      </Card>

      <Card variant="outline">
        <Card.Header>
          <div className="flex items-center gap-3">
            <Icon icon={UserRoundCog} size="md" />
            <Typography variant="heading-4" as="h2">
              What a human has to do
            </Typography>
          </div>
        </Card.Header>
        <Card.Body>
          <ol className="flex flex-col gap-4">
            {GRANT_STEPS.map((step, index) => (
              <li key={step.title} className="flex gap-4">
                <span className="bg-bg-muted text-fg flex size-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold tabular-nums">
                  {index + 1}
                </span>
                <div className="flex flex-col gap-1">
                  <Typography variant="label" as="span">
                    {step.title}
                  </Typography>
                  <Typography variant="body-sm" tone="muted">
                    {step.detail}
                  </Typography>
                </div>
              </li>
            ))}
          </ol>
        </Card.Body>
      </Card>

      <Card variant="outline">
        <Card.Header>
          <div className="flex items-center gap-3">
            <Icon icon={ListChecks} size="md" />
            <Typography variant="heading-4" as="h2">
              What this board deliberately does not ask for
            </Typography>
          </div>
        </Card.Header>
        <Card.Body>
          <div className="flex flex-col gap-4">
            <Typography variant="body-sm" tone="muted">
              Shipped in the payload rather than kept in a document, so the reasoning is visible to
              whoever next wonders why there is no device panel — and so an exclusion cannot be
              quietly re-added by the next person who wants one more chart.
            </Typography>
            <ul className="flex flex-col gap-4">
              {permissions.excluded.map((excluded) => (
                <li key={excluded.name} className="flex gap-3">
                  <div className="pt-0.5">
                    <Icon icon={NEVER_REQUESTED_ICON} size="sm" tone="danger" />
                  </div>
                  <div className="flex flex-col gap-1">
                    <code className="text-fg font-mono text-xs break-all">{excluded.name}</code>
                    <Typography variant="body-sm" tone="muted">
                      {excluded.reason}
                    </Typography>
                  </div>
                </li>
              ))}
            </ul>
            <Separator />
            <Typography variant="body-sm" tone="muted">
              Nothing on this board is simulated and no figure on this page is seeded. Until the
              permissions are granted every number reads as absent rather than as zero, which is the
              distinction the rest of this board is built on.
            </Typography>
          </div>
        </Card.Body>
      </Card>
    </section>
  );
}

/**
 * Merge what the setup route observed with what the panels measured this render.
 *
 * `observed` comes from a process-wide registry the panels write to, so within a
 * single page load the panels can know about a 403 before this route reports it.
 * A refusal from either source is a refusal; everything else that is not an
 * outright success stays `unprobed` — the honest state before any call.
 */
function verdictFor(
  entry: DefenderPermissionStatus,
  refused: ReadonlySet<string>,
): PermissionVerdict {
  if (entry.observed === "denied" || refused.has(entry.name)) {
    return "refused";
  }
  return entry.observed === "granted" ? "granted" : "unprobed";
}

interface PermissionRowProps {
  entry: DefenderPermissionStatus;
  verdict: PermissionVerdict;
}

/** One permission: what it is, what it unlocks, and what a read has proved. */
function PermissionRow({ entry, verdict }: PermissionRowProps) {
  const copy = VERDICT_COPY[verdict];
  return (
    <div className="flex gap-4">
      <div className="pt-0.5">
        <Icon icon={copy.icon} size="md" tone={copy.iconTone} />
      </div>
      <div className="flex flex-1 flex-col gap-1.5">
        <div className="flex flex-wrap items-center gap-2">
          <code className="text-fg bg-bg-muted rounded-sm px-1.5 py-0.5 font-mono text-xs break-all">
            {entry.name}
          </code>
          {/* Never colour-alone (§3.2) — the badge says it in words. */}
          <Badge tone={copy.tone} variant="subtle" size="sm">
            {copy.badge}
          </Badge>
          <Badge tone="neutral" variant="outline" size="xs">
            Application · read-only
          </Badge>
        </div>
        <Typography variant="body-sm" as="span">
          {entry.panel}
        </Typography>
        <Typography variant="body-sm" tone="muted">
          {copy.note}
        </Typography>
        <div className="flex flex-wrap gap-x-3 gap-y-1">
          {entry.endpoints.map((endpoint) => (
            <code key={endpoint} className="text-fg-muted font-mono text-xs">
              GET {endpoint}
            </code>
          ))}
        </div>
        {/* Provenance: the row of TODO 2.5 this entry was transcribed from, so a
            reviewer can diff the ask against the research it came from. */}
        <Typography variant="caption-sm" tone="muted">
          {entry.todo_line}
        </Typography>
      </div>
    </div>
  );
}
